import { getDatabase, ref } from "firebase/database";
import { getFirebaseApp, isFirebaseConfigured, onValue, set, update, get } from "./firebaseClient";

function lobbyKey(lobbyName) {
  return String(lobbyName || "")
    .trim()
    .replace(/[.#$[\]]/g, "_");
}

export function scribbleLobbyRef(lobbyName) {
  const app = getFirebaseApp();
  if (!app) return null;
  const db = getDatabase(app);
  return ref(db, `scribble_lobbies/${lobbyKey(lobbyName)}`);
}

export function subscribeScribbleLobby(lobbyName, onData, onError) {
  if (!isFirebaseConfigured() || !lobbyName) {
    onData(null);
    return () => {};
  }
  const r = scribbleLobbyRef(lobbyName);
  if (!r) return () => {};
  return onValue(
    r,
    (snap) => onData(snap.val() || null),
    (err) => {
      console.warn("[scribble] RTDB error:", err?.message || err);
      if (onError) onError(err);
    }
  );
}

export async function getScribbleLobby(lobbyName) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) return null;
  const snap = await get(r);
  return snap.val() || null;
}

function shuffleIds(ids) {
  const arr = [...ids];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export async function initScribbleLobby({
  lobbyName,
  hostUserId,
  roundCount = 3,
  maxPlayers = 10,
  timerSeconds = 60,
  gameMode = "manager",
}) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const raw = String(gameMode || "manager").toLowerCase();
  const mode = raw === "taboo" ? "taboo" : raw === "anchor" ? "anchor" : "manager";
  await set(r, {
    phase: "lobby",
    gameMode: mode,
    hostUserId: String(hostUserId),
    roundCount: Math.max(1, Number(roundCount) || 3),
    maxPlayers: Math.max(1, Number(maxPlayers) || 10),
    timerSeconds: Math.max(30, Number(timerSeconds) || 60),
    players: {},
    drawers: [],
    updatedAt: Date.now(),
  });
}

export async function upsertScribblePlayer(lobbyName, player) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const id = String(player.id);
  await update(r, {
    [`players/${id}`]: {
      name: String(player.name || "Player").trim() || "Player",
      avatarIndex: Number(player.avatarIndex) || 1,
      isHost: Boolean(player.isHost),
      joinedAt: player.joinedAt || Date.now(),
    },
    updatedAt: Date.now(),
  });
}

export async function setScribblePhase(lobbyName, phase) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  await update(r, { phase, updatedAt: Date.now() });
}

export async function setScribbleDrawers(lobbyName, drawers) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  await update(r, {
    drawers: Array.isArray(drawers) ? drawers.map((d) => String(d)) : [],
    updatedAt: Date.now(),
  });
}

/** Manager mode: random turn order — every joined player draws once. */
export async function startManagerScribbleGame(lobbyName, playerIds) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const existing = await getScribbleLobby(lobbyName);
  const ids = shuffleIds(
    (Array.isArray(playerIds) ? playerIds : []).map(String).filter(Boolean)
  );
  if (ids.length === 0) throw new Error("Need at least one player to start.");
  const timerSeconds = Math.max(30, Number(existing?.timerSeconds) || 60);
  const firstDrawer = ids[0];
  await update(r, {
    phase: "drawing",
    gameMode: "manager",
    roundCount: ids.length,
    drawers: ids,
    game: {
      turnIndex: 0,
      turnPhase: "pick_word",
      currentDrawerId: firstDrawer,
      timerSeconds,
      timerEndsAt: null,
      selectedWord: "",
      selectedHint: "",
      guessedBy: null,
      guessedByName: "",
      guessedPoints: 0,
    },
    canvas: { strokes: {} },
    chat: {},
    updatedAt: Date.now(),
  });
}

/** Anchor mode: host picks one drawer for the current round, then play begins. */
export async function setAnchorDrawerForRound(lobbyName, drawerId) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const existing = await getScribbleLobby(lobbyName);
  const game = existing?.game || {};
  const turnIndex = Number(game.turnIndex) || 0;
  const prevDrawers = Array.isArray(existing?.drawers) ? existing.drawers.map(String) : [];
  const drawers = [...prevDrawers, String(drawerId)];
  const timerSeconds = Math.max(30, Number(existing?.timerSeconds) || 60);
  await update(r, {
    phase: "drawing",
    gameMode: "anchor",
    drawers,
    game: {
      turnIndex,
      turnPhase: "pick_word",
      currentDrawerId: String(drawerId),
      timerSeconds,
      timerEndsAt: null,
      selectedWord: "",
      selectedHint: "",
      guessedBy: null,
      guessedByName: "",
      guessedPoints: 0,
    },
    canvas: { strokes: {} },
    chat: {},
    updatedAt: Date.now(),
  });
}

/** Anchor mode: open drawer picker before the first round or between rounds. */
export async function openAnchorDrawerSelection(lobbyName) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  await update(r, {
    phase: "select_drawer",
    updatedAt: Date.now(),
  });
}

export async function startScribbleGame(lobbyName) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const existing = await getScribbleLobby(lobbyName);
  const drawers = Array.isArray(existing?.drawers) ? existing.drawers.map(String) : [];
  const firstDrawer = drawers[0] || "";
  const timerSeconds = Math.max(30, Number(existing?.timerSeconds) || 60);
  await update(r, {
    phase: "drawing",
    game: {
      turnIndex: 0,
      turnPhase: "pick_word",
      currentDrawerId: firstDrawer,
      timerSeconds,
      timerEndsAt: null,
      selectedWord: "",
      selectedHint: "",
      guessedBy: null,
      guessedByName: "",
      guessedPoints: 0,
    },
    canvas: { strokes: {} },
    chat: {},
    updatedAt: Date.now(),
  });
}
